/**
 * Payment Permissions Management Component
 * Allows Admin/SuperAdmin to manage payment processing permissions for users
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { DollarSign, Check, X, AlertCircle, CreditCard } from 'lucide-react';
import { toast } from 'react-toastify';

const PaymentPermissions = () => {
  const API = process.env.REACT_APP_BACKEND_URL || '';
  
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmModal, setConfirmModal] = useState(null);
  const [updating, setUpdating] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.get(`${API}/api/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setUsers(response.data.users || []);
      }
    } catch (error) {
      console.error('Kullanıcılar yüklenemedi:', error);
      toast.error(error.response?.data?.detail || 'Kullanıcılar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const togglePaymentPermission = async (user) => {
    setConfirmModal({
      user,
      newValue: !user.can_process_payments
    });
  };

  const confirmPermissionChange = async () => {
    if (!confirmModal) return;

    try {
      setUpdating(confirmModal.user.id);
      const token = localStorage.getItem('token');
      
      const response = await axios.patch(
        `${API}/api/users/${confirmModal.user.id}/payment-permissions`,
        {
          can_process_payments: confirmModal.newValue
        },
        {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        toast.success(response.data.message);
        await fetchUsers();
        setConfirmModal(null);
      }
    } catch (error) {
      console.error('Ödeme yetkisi güncelleme hatası:', error);
      toast.error(error.response?.data?.detail || 'Ödeme yetkisi güncellenemedi');
    } finally {
      setUpdating(null);
    }
  };

  const getRoleBadgeColor = (roleName) => {
    const role = roleName?.toLowerCase() || '';
    if (role.includes('super') || role.includes('süper')) {
      return 'bg-red-100 text-red-700 border-red-200';
    } else if (role.includes('admin')) {
      return 'bg-orange-100 text-orange-700 border-orange-200';
    } else if (role.includes('accounting') || role.includes('muhasebe')) {
      return 'bg-green-100 text-green-700 border-green-200';
    } else {
      return 'bg-blue-100 text-blue-700 border-blue-200';
    }
  };

  const getRoleDisplayName = (roleName) => {
    const role = roleName?.toLowerCase() || '';
    if (role.includes('super') || role.includes('süper')) {
      return 'Süper Admin';
    } else if (role.includes('admin')) {
      return 'Admin';
    } else if (role.includes('accounting') || role.includes('muhasebe')) {
      return 'Muhasebe';
    } else {
      return 'Kullanıcı';
    }
  };

  const isProtectedRole = (roleName) => {
    const role = roleName?.toLowerCase() || '';
    return role.includes('super') || role.includes('süper') || 
           role.includes('accounting') || role.includes('muhasebe');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 flex items-center">
          <DollarSign className="mr-2 text-teal-600" />
          Ödeme İşleme Yetkileri
        </h2>
        <p className="text-gray-600 mt-2">
          Kullanıcılara ödeme işleme yetkisi verebilir veya kaldırabilirsiniz. 
          Süper Admin, Admin ve Muhasebe rollerinin varsayılan olarak ödeme yetkisi vardır.
        </p>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Kullanıcı
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Rol
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Email
              </th>
              <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ödeme Yetkisi
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                  <AlertCircle className="mx-auto mb-2 text-gray-400" size={32} />
                  Kullanıcı bulunamadı
                </td>
              </tr>
            ) : (
              users.map((user) => {
                const isProtected = isProtectedRole(user.role_name);
                const hasPaymentPermission = user.can_process_payments || isProtected;

                return (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-teal-100 rounded-full flex items-center justify-center">
                          <span className="text-teal-600 font-medium text-sm">
                            {user.full_name?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.full_name}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getRoleBadgeColor(user.role_name)}`}>
                        {getRoleDisplayName(user.role_name)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {isProtected ? (
                        <div className="inline-flex items-center text-sm text-gray-600">
                          <Check className="mr-1 text-green-600" size={16} />
                          <span className="text-green-600 font-medium">Varsayılan Yetki</span>
                        </div>
                      ) : (
                        <button
                          onClick={() => togglePaymentPermission(user)}
                          disabled={updating === user.id}
                          className={`
                            relative inline-flex h-6 w-11 items-center rounded-full
                            transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2
                            ${hasPaymentPermission ? 'bg-teal-600' : 'bg-gray-200'}
                            ${updating === user.id ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
                          `}
                        >
                          <span
                            className={`
                              inline-block h-4 w-4 transform rounded-full bg-white transition-transform
                              ${hasPaymentPermission ? 'translate-x-6' : 'translate-x-1'}
                            `}
                          />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Confirmation Modal */}
      {confirmModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <CreditCard className="mr-2 text-teal-600" />
                Ödeme Yetkisi Güncelleme
              </h3>
            </div>
            <div className="px-6 py-4">
              <p className="text-gray-700">
                <strong>{confirmModal.user.full_name}</strong> kullanıcısının ödeme işleme yetkisini
                <span className={confirmModal.newValue ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                  {confirmModal.newValue ? ' vermek' : ' kaldırmak'}
                </span> istediğinizden emin misiniz?
              </p>
              {confirmModal.newValue && (
                <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                  <p className="text-sm text-blue-800 flex items-start">
                    <AlertCircle className="mr-2 flex-shrink-0 mt-0.5" size={16} />
                    Bu kullanıcı belge ödemelerini işleyebilecek.
                  </p>
                </div>
              )}
            </div>
            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
              <button
                onClick={() => setConfirmModal(null)}
                disabled={updating}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50"
              >
                İptal
              </button>
              <button
                onClick={confirmPermissionChange}
                disabled={updating}
                className="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50"
              >
                {updating ? 'Güncelleniyor...' : 'Onayla'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentPermissions;
